import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FlaskConical, Plus, Trash, History, Info, ShoppingCart, Leaf, ArrowRight, CheckCircle, AlertTriangle, XCircle, BookOpen, GraduationCap, ExternalLink } from 'lucide-react';
import { DepartmentNav } from '../components/DepartmentNav';
import { AmazonDisclosure } from '../components/AmazonDisclosure';
import { UpsellStrip } from '../components/UpsellStrip';
import { PageWhy } from '../components/PageWhy';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// Severity badge component
const SeverityBadge = ({ severity }) => {
  const styles = {
    high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  };
  
  const icons = {
    high: <XCircle className="w-3 h-3" />,
    medium: <AlertTriangle className="w-3 h-3" />,
    low: <CheckCircle className="w-3 h-3" />
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[severity] || styles.low}`}>
      {icons[severity]}
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
};

export const SoilAnalysisPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [zones, setZones] = useState([]);
  const [testHistory, setTestHistory] = useState([]);
  const [optimalRanges, setOptimalRanges] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [showHistoryDetail, setShowHistoryDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('analyze');
  
  // Form state
  const [formData, setFormData] = useState({
    ph: '',
    nitrogen_ppm: '',
    phosphorus_ppm: '',
    potassium_ppm: '',
    organic_matter_percent: '',
    zone_id: ''
  });
  const [showCustomZone, setShowCustomZone] = useState(false);
  const [customZoneName, setCustomZoneName] = useState('');
  const [creatingZone, setCreatingZone] = useState(false);

  useEffect(() => {
    fetchOptimalRanges();
    fetchTestHistory();
    fetchZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOptimalRanges = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/soil/optimal-ranges`);
      setOptimalRanges(res.data);
    } catch (error) {
      console.error('Failed to fetch optimal ranges:', error);
    }
  };

  const fetchTestHistory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/soil/history`, { withCredentials: true });
      setTestHistory(res.data.tests || []);
    } catch (error) {
      console.error('Failed to fetch test history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/property/zones`, { withCredentials: true });
      setZones(res.data || []);
    } catch (error) {
      console.error('Failed to fetch zones:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleZoneChange = (value) => {
    if (value === "create_new") {
      setShowCustomZone(true);
      setFormData(prev => ({ ...prev, zone_id: '' }));
    } else {
      setShowCustomZone(false);
      setCustomZoneName('');
      handleInputChange('zone_id', value === "none" ? "" : value);
    }
  };

  const handleCreateCustomZone = async () => {
    if (!customZoneName.trim()) {
      toast.error('Please enter a zone name');
      return;
    }

    setCreatingZone(true);
    try {
      // First check if property exists, if not create one
      const propRes = await axios.get(`${API_URL}/api/property`, { withCredentials: true });
      if (!propRes.data) {
        // Create a default property first
        await axios.post(`${API_URL}/api/property`, {
          name: "My Property"
        }, { withCredentials: true });
      }

      // Create the zone
      const res = await axios.post(`${API_URL}/api/property/zones`, {
        name: customZoneName.trim(),
        grass_type: null,
        sun_exposure: null,
        square_footage: null,
        notes: "Created from Soil Analysis"
      }, { withCredentials: true });

      // Add to zones list and select it
      setZones(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, zone_id: res.data.id }));
      setShowCustomZone(false);
      setCustomZoneName('');
      toast.success(`Zone "${res.data.name}" created!`);
    } catch (error) {
      console.error('Failed to create zone:', error);
      toast.error(error.response?.data?.detail || 'Failed to create zone');
    } finally {
      setCreatingZone(false);
    }
  };

  const handleAnalyze = async () => {
    // Validation ranges
    const ranges = {
      ph: { min: 3.0, max: 10.0, name: 'pH' },
      nitrogen_ppm: { min: 0, max: 200, name: 'Nitrogen' },
      phosphorus_ppm: { min: 0, max: 500, name: 'Phosphorus' },
      potassium_ppm: { min: 0, max: 1000, name: 'Potassium' },
      organic_matter_percent: { min: 0, max: 20, name: 'Organic Matter' }
    };

    // Validate pH (required)
    if (!formData.ph || isNaN(parseFloat(formData.ph))) {
      toast.error('Please enter a valid pH value');
      return;
    }
    
    const ph = parseFloat(formData.ph);
    if (ph < ranges.ph.min || ph > ranges.ph.max) {
      toast.error(`pH must be between ${ranges.ph.min} and ${ranges.ph.max}. Check your lab report.`);
      return;
    }

    // Validate optional fields if provided
    for (const [field, range] of Object.entries(ranges)) {
      if (field === 'ph') continue; // Already validated
      const value = formData[field];
      if (value && value !== '') {
        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < range.min || numValue > range.max) {
          toast.error(`${range.name} must be between ${range.min} and ${range.max}. Check your lab report.`);
          return;
        }
      }
    }

    setAnalyzing(true);
    try {
      const payload = {
        ph: parseFloat(formData.ph),
        nitrogen_ppm: formData.nitrogen_ppm ? parseFloat(formData.nitrogen_ppm) : null,
        phosphorus_ppm: formData.phosphorus_ppm ? parseFloat(formData.phosphorus_ppm) : null,
        potassium_ppm: formData.potassium_ppm ? parseFloat(formData.potassium_ppm) : null,
        organic_matter_percent: formData.organic_matter_percent ? parseFloat(formData.organic_matter_percent) : null,
        zone_id: formData.zone_id || null
      };
      
      const res = await axios.post(`${API_URL}/api/soil/analyze`, payload, { withCredentials: true });
      setAnalysisResult(res.data);
      setShowTestDialog(false);
      setActiveTab('results');
      toast.success('Soil analysis complete!');
      fetchTestHistory(); // Refresh history
    } catch (error) {
      console.error('Failed to analyze soil:', error);
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors
        toast.error(detail[0]?.msg || 'Invalid input values. Check your entries.');
      } else {
        toast.error(detail || 'Failed to analyze soil test');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDeleteTest = async (testId) => {
    if (!window.confirm('Are you sure you want to delete this soil test?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/soil/history/${testId}`, { withCredentials: true });
      toast.success('Soil test deleted');
      fetchTestHistory();
      if (showHistoryDetail?.id === testId) {
        setShowHistoryDetail(null);
      }
    } catch (error) {
      toast.error('Failed to delete soil test');
    }
  };

  const resetForm = () => {
    setFormData({
      ph: '',
      nitrogen_ppm: '',
      phosphorus_ppm: '',
      potassium_ppm: '',
      organic_matter_percent: '',
      zone_id: ''
    });
    setShowCustomZone(false);
    setCustomZoneName('');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="soil-analysis-page">
      <DepartmentNav />
      <main id="main-content" className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <FlaskConical className="w-8 h-8 text-emerald-600" />
              Soil Analysis
            </h1>
            <p className="text-muted-foreground mt-1">
              Enter your soil test results to get personalized product recommendations
            </p>
            <PageWhy testId="soil-page-why">Stop guessing what your lawn needs. Enter your lab numbers and get the exact amendments and rates — so you spend on what actually moves the needle.</PageWhy>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => window.location.assign('/soil-test')}
              data-testid="soil-pdf-parser-link"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              Upload PDF instead
            </Button>
            <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => { resetForm(); setShowTestDialog(true); }}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="new-soil-test-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Soil Test
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-emerald-600" />
                  Enter Soil Test Results
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Validation Info Banner */}
                <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-700 dark:text-blue-400">
                    <Info className="w-3 h-3 inline mr-1" />
                    Enter values exactly as shown on your soil lab report. Invalid values will be rejected to ensure accurate recommendations.
                  </p>
                </div>

                {/* Zone Selection */}
                <div className="space-y-2">
                  <Label>Zone (Optional)</Label>
                  {!showCustomZone ? (
                    <Select 
                      value={formData.zone_id || "none"} 
                      onValueChange={handleZoneChange}
                    >
                      <SelectTrigger data-testid="zone-select">
                        <SelectValue placeholder="Select a zone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No zone</SelectItem>
                        {zones.map(zone => (
                          <SelectItem key={zone.id} value={zone.id}>{zone.name}</SelectItem>
                        ))}
                        <SelectItem value="create_new" className="text-emerald-600 font-medium">
                          <span className="flex items-center gap-1">
                            <Plus className="w-3 h-3" />
                            Create new zone...
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g., Sidewalk Strip, Backyard, Front Lawn"
                          value={customZoneName}
                          onChange={(e) => setCustomZoneName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleCreateCustomZone()}
                          data-testid="custom-zone-input"
                          autoFocus
                        />
                        <Button 
                          type="button"
                          size="sm"
                          onClick={handleCreateCustomZone}
                          disabled={creatingZone}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {creatingZone ? '...' : 'Add'}
                        </Button>
                        <Button 
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setShowCustomZone(false);
                            setCustomZoneName('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Enter a name for your new zone (e.g., "Sidewalk Strip", "Shady Corner")
                      </p>
                    </div>
                  )}
                </div>
                
                {/* pH (Required) */}
                <div className="space-y-2">
                  <Label htmlFor="ph" className="flex items-center gap-1">
                    pH Level <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="ph"
                    type="number"
                    step="0.1"
                    min="3.0"
                    max="10.0"
                    placeholder="e.g., 6.5"
                    value={formData.ph}
                    onChange={(e) => handleInputChange('ph', e.target.value)}
                    data-testid="ph-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Accepted: 3.0 - 10.0 | Optimal: {optimalRanges?.optimal_ranges?.ph?.low || 6.0} - {optimalRanges?.optimal_ranges?.ph?.high || 7.0}
                  </p>
                </div>
                
                {/* Nitrogen */}
                <div className="space-y-2">
                  <Label htmlFor="nitrogen">Nitrogen (ppm)</Label>
                  <Input
                    id="nitrogen"
                    type="number"
                    step="1"
                    min="0"
                    max="200"
                    placeholder="e.g., 35"
                    value={formData.nitrogen_ppm}
                    onChange={(e) => handleInputChange('nitrogen_ppm', e.target.value)}
                    data-testid="nitrogen-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Accepted: 0 - 200 ppm | Optimal: {optimalRanges?.optimal_ranges?.nitrogen_ppm?.low || 25} - {optimalRanges?.optimal_ranges?.nitrogen_ppm?.high || 50} ppm
                  </p>
                </div>
                
                {/* Phosphorus */}
                <div className="space-y-2">
                  <Label htmlFor="phosphorus">Phosphorus (ppm)</Label>
                  <Input
                    id="phosphorus"
                    type="number"
                    step="1"
                    min="0"
                    max="500"
                    placeholder="e.g., 30"
                    value={formData.phosphorus_ppm}
                    onChange={(e) => handleInputChange('phosphorus_ppm', e.target.value)}
                    data-testid="phosphorus-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Accepted: 0 - 500 ppm | Optimal: {optimalRanges?.optimal_ranges?.phosphorus_ppm?.low || 25} - {optimalRanges?.optimal_ranges?.phosphorus_ppm?.high || 50} ppm
                  </p>
                </div>
                
                {/* Potassium */}
                <div className="space-y-2">
                  <Label htmlFor="potassium">Potassium (ppm)</Label>
                  <Input
                    id="potassium"
                    type="number"
                    step="1"
                    min="0"
                    max="1000"
                    placeholder="e.g., 200"
                    value={formData.potassium_ppm}
                    onChange={(e) => handleInputChange('potassium_ppm', e.target.value)}
                    data-testid="potassium-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Accepted: 0 - 1000 ppm | Optimal: {optimalRanges?.optimal_ranges?.potassium_ppm?.low || 150} - {optimalRanges?.optimal_ranges?.potassium_ppm?.high || 250} ppm
                  </p>
                </div>
                
                {/* Organic Matter */}
                <div className="space-y-2">
                  <Label htmlFor="organic_matter">Organic Matter (%)</Label>
                  <Input
                    id="organic_matter"
                    type="number"
                    step="0.1"
                    min="0"
                    max="20"
                    placeholder="e.g., 3.5"
                    value={formData.organic_matter_percent}
                    onChange={(e) => handleInputChange('organic_matter_percent', e.target.value)}
                    data-testid="organic-matter-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Accepted: 0 - 20% | Optimal: {optimalRanges?.optimal_ranges?.organic_matter_percent?.low || 2} - {optimalRanges?.optimal_ranges?.organic_matter_percent?.high || 5}%
                  </p>
                </div>
                
                <Button 
                  onClick={handleAnalyze} 
                  disabled={analyzing}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="analyze-btn"
                >
                  {analyzing ? 'Analyzing...' : 'Analyze & Get Recommendations'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="mb-6">
          <UpsellStrip
            feature="soil_analysis"
            headline="Save every soil test, season to season."
            body="On Enthusiast Annual you get unlimited test history, zone-by-zone tracking, and recommendations that learn from your pattern over time."
            ctaLabel="Unlock Full History"
            testId="soil-upsell-strip"
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="analyze" data-testid="analyze-tab">
              <FlaskConical className="w-4 h-4 mr-2" />
              Analyze
            </TabsTrigger>
            <TabsTrigger value="results" data-testid="results-tab">
              <Leaf className="w-4 h-4 mr-2" />
              Results
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Analyze Tab */}
          <TabsContent value="analyze" className="space-y-6">
            {/* Info Cards */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {optimalRanges && optimalRanges.optimal_ranges && Object.entries(optimalRanges.optimal_ranges).map(([key, range]) => (
                <Card key={key} className="border-l-4 border-l-emerald-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg capitalize">
                      {key.replace(/_/g, ' ').replace('ppm', '').replace('percent', '')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl font-bold text-emerald-600">
                        {range.ideal}{key.includes('percent') ? '%' : key.includes('ppm') ? ' ppm' : ''}
                      </span>
                      <span className="text-sm text-muted-foreground">Ideal</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Optimal: {range.low} - {range.high}{key.includes('percent') ? '%' : key.includes('ppm') ? ' ppm' : ''}
                    </div>
                    {optimalRanges.accepted_ranges && optimalRanges.accepted_ranges[key] && (
                      <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                        Accepted: {optimalRanges.accepted_ranges[key].min} - {optimalRanges.accepted_ranges[key].max}{optimalRanges.accepted_ranges[key].unit}
                      </div>
                    )}
                    {optimalRanges.description && optimalRanges.description[key] && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {optimalRanges.description[key]}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Testing Tips */}
            {optimalRanges?.testing_tips && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-500" />
                    Soil Testing Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {optimalRanges.testing_tips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Data Sources Card */}
            {optimalRanges?.sources && (
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    Data Sources
                  </CardTitle>
                  <CardDescription>
                    Our recommendations are based on peer-reviewed research from accredited University Extension services
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {optimalRanges.sources.primary?.map((source, idx) => (
                      <a
                        key={idx}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors group"
                      >
                        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                          <GraduationCap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{source.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{source.title}</div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                  
                  {optimalRanges.sources.note && (
                    <p className="text-xs text-muted-foreground border-t pt-3">
                      {optimalRanges.sources.note}
                    </p>
                  )}
                  
                  {optimalRanges.disclaimer && (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {optimalRanges.disclaimer}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="space-y-6">
            {analysisResult ? (
              <>
                {/* Summary Card */}
                <Card className="border-l-4 border-l-emerald-500">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Analysis Summary</span>
                      {analysisResult.zone_name && (
                        <span className="text-sm font-normal bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-1 rounded">
                          {analysisResult.zone_name}
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription>
                      Tested on {formatDate(analysisResult.tested_at)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-3xl font-bold text-foreground">
                          {analysisResult.summary.total_issues}
                        </div>
                        <div className="text-sm text-muted-foreground">Total Issues</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-red-500">
                          {analysisResult.summary.high_priority}
                        </div>
                        <div className="text-sm text-muted-foreground">High Priority</div>
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-amber-500">
                          {analysisResult.summary.medium_priority}
                        </div>
                        <div className="text-sm text-muted-foreground">Medium Priority</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                <div className="space-y-4">
                  {analysisResult.analysis.map((item, idx) => (
                    <Card key={idx} data-testid={`recommendation-${idx}`}>
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            {item.issue_type === 'optimal' ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <AlertTriangle className="w-5 h-5 text-amber-500" />
                            )}
                            {item.issue}
                          </span>
                          <SeverityBadge severity={item.severity} />
                        </CardTitle>
                        {item.current_value !== null && (
                          <CardDescription>
                            Current: <strong>{item.current_value}</strong> | Optimal: {item.optimal_range}
                          </CardDescription>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                          {item.recommendation}
                        </p>
                        
                        {/* Product Recommendations */}
                        {item.products && item.products.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-medium text-sm flex items-center gap-2">
                              <ShoppingCart className="w-4 h-4" />
                              Recommended Products
                            </h4>
                            <div className="grid gap-3">
                              {item.products.map((product, pIdx) => (
                                <div 
                                  key={pIdx}
                                  className="p-3 rounded-lg bg-muted/50 border"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <h5 className="font-medium text-sm">{product.name}</h5>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {product.description}
                                      </p>
                                      {product.application_rate && (
                                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                                          Rate: {product.application_rate}
                                        </p>
                                      )}
                                    </div>
                                    {product.affiliate_link && (
                                      <a
                                        href={product.affiliate_link}
                                        target="_blank"
                                        rel="nofollow sponsored noopener noreferrer"
                                        className="flex-shrink-0"
                                      >
                                        <Button size="sm" variant="outline" className="text-xs">
                                          <ShoppingCart className="w-3 h-3 mr-1" />
                                          Buy
                                        </Button>
                                      </a>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Amazon Disclosure */}
                <AmazonDisclosure />
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <FlaskConical className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Analysis Results</h3>
                  <p className="text-muted-foreground mb-4">
                    Submit a soil test to see recommendations here
                  </p>
                  <Button onClick={() => setShowTestDialog(true)} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    New Soil Test
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            {loading ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Loading history...
                </CardContent>
              </Card>
            ) : testHistory.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Test History</h3>
                  <p className="text-muted-foreground mb-4">
                    Your soil test history will appear here
                  </p>
                  <Button onClick={() => setShowTestDialog(true)} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    New Soil Test
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {testHistory.map((test) => (
                  <Card 
                    key={test.id} 
                    className="cursor-pointer hover:border-emerald-500 transition-colors"
                    onClick={() => {
                      setAnalysisResult({
                        ...test,
                        analysis: test.recommendations,
                        summary: {
                          total_issues: test.recommendations.filter(r => r.issue_type !== 'optimal').length,
                          high_priority: test.recommendations.filter(r => r.severity === 'high').length,
                          medium_priority: test.recommendations.filter(r => r.severity === 'medium').length
                        }
                      });
                      setActiveTab('results');
                    }}
                    data-testid={`history-item-${test.id}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                            <FlaskConical className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <div className="font-medium">
                              {test.zone_name || 'General Test'}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatDate(test.tested_at)} • pH: {test.ph}
                              {test.nitrogen_ppm && ` • N: ${test.nitrogen_ppm}`}
                              {test.phosphorus_ppm && ` • P: ${test.phosphorus_ppm}`}
                              {test.potassium_ppm && ` • K: ${test.potassium_ppm}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {test.recommendations?.filter(r => r.issue_type !== 'optimal').length || 0} issues
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTest(test.id);
                            }}
                            data-testid={`delete-test-${test.id}`}
                          >
                            <Trash className="w-4 h-4 text-red-500" />
                          </Button>
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SoilAnalysisPage;
